import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  FiClock,
  FiCopy,
  FiCornerUpLeft,
  FiFileText,
  FiHelpCircle,
  FiMessageSquare,
  FiSave,
  FiSend,
  FiSettings,
} from "react-icons/fi";
import Layout from "../../../components/layouts/Layout";
import {
  duplicateAutomationFlow,
  fetchAutomationFlow,
  fetchLiveEvents,
  pauseAutomationFlow,
  publishAutomationFlow,
  updateAutomationFlow,
} from "./automationTemplateApi";
import SmsAccessGuard from "../Components/SmsAccessGuard";
import LocalStorage from "../../../hook/LocalStorage";
import InitialMessageSendTimeSelect from "./InitialMessageSendTimeSelect";
import {
  DEFAULT_INITIAL_MESSAGE_SEND_TIME,
  normaliseInitialMessageSendTime,
} from "./automationConstants";
import { normaliseAutomationFlowStatus } from "./automationFlowStatus";
import "../style/sms-billing.css";

const FLOW_STATUS = {
  active: "Active",
  inactive: "Inactive",
};

const MESSAGE_CHARACTER_LIMIT = 160;
const DELAY_TYPE_OPTIONS = ["immediate", "days"];
const ROI_CATEGORY_OPTIONS = [
  ["function_enquiry", "Function enquiry"],
  ["function_package_request", "Function package request"],
  ["table_booking", "Table booking"],
  ["membership", "Membership"],
  ["event_ticket", "Event ticket"],
  ["guest_issue", "Guest issue"],
  ["staff_contact", "Staff contact"],
  ["other", "Other"],
];

const limitMessageLength = (value) => String(value || "").slice(0, MESSAGE_CHARACTER_LIMIT);

const normaliseDelayType = (value) => {
  const delayType = String(value || "").toLowerCase();

  return DELAY_TYPE_OPTIONS.includes(delayType) ? delayType : "immediate";
};

const SECTION_FIELDS = {
  replyRules: [
    "reply_rules",
    "replyRules",
    "rules",
    "keywords",
    "reply_keywords",
    "replyKeywords",
  ],
  ruleActions: ["rule_actions", "ruleActions", "actions", "automation_actions", "automationActions"],
  reminderActions: ["reminder_actions", "reminderActions", "reminders"],
  notificationActions: ["notification_actions", "notificationActions", "notifications"],
};

const readFirstDefined = (source, keys) => {
  if (!source) {
    return null;
  }

  const key = keys.find((item) => source[item] !== undefined && source[item] !== null);

  return key ? source[key] : null;
};

const findFieldKey = (source, keys) => {
  if (!source) {
    return null;
  }

  return keys.find((item) => source[item] !== undefined && source[item] !== null) || null;
};

const extractFlow = (payload) => {
  const source = payload?.data || payload || {};

  return source.flow || source.automation_flow || source.automationFlow || source.data || source;
};

const toReadableLabel = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not configured";
  }

  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

const ACTION_TYPE_LABELS = {
  notification: "In-App Notification",
  send_sms: "Send SMS",
  send_email: "Send Email",
  create_reminder: "Create Reminder",
};

const formatActionType = (actionType) => ACTION_TYPE_LABELS[actionType] || toReadableLabel(actionType);

const formatStatus = (status) => FLOW_STATUS[status] || "Inactive";

const isPlainObject = (value) => {
  return value && typeof value === "object" && !Array.isArray(value);
};

const getFieldKey = (source, keys, fallback) => findFieldKey(source, keys) || fallback;

const ACTION_KEYS = ["actions", "rule_actions", "ruleActions", "automation_rule_actions", "automationRuleActions", "reply_actions", "replyActions"];

const getRuleActions = (rule) => {
  const actions = readFirstDefined(rule, ACTION_KEYS);

  return Array.isArray(actions) ? actions : normaliseCollection(actions);
};

const normalizePayload = (payload) => {
  if (isPlainObject(payload)) {
    return payload;
  }

  if (typeof payload === "string") {
    try {
      const parsedPayload = JSON.parse(payload);

      return isPlainObject(parsedPayload) ? parsedPayload : {};
    } catch (error) {
      return {};
    }
  }

  return {};
};

const getPayloadMessage = (action) => {
  const payload = normalizePayload(readFirstDefined(action, ["action_payload", "actionPayload", "payload", "data"]));

  return readFirstDefined(payload, ["message", "body", "sms_message", "smsMessage"]) ||
    readFirstDefined(action, ["message", "body", "sms_message", "smsMessage"]) ||
    "";
};

const isSendSmsAction = (actionType) => String(actionType || "").toLowerCase() === "send_sms";

const getValidationMessage = (error, field) => {
  const responseData = error?.response?.data || {};
  const fieldError = responseData?.errors?.[field];

  if (Array.isArray(fieldError)) {
    return fieldError[0] || "";
  }

  if (typeof fieldError === "string") {
    return fieldError;
  }

  return "";
};

const updateCollectionAtIndex = (collection, index, updater) => {
  if (Array.isArray(collection)) {
    return collection.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }

      return updater(isPlainObject(item) ? { ...item } : item);
    });
  }

  if (isPlainObject(collection)) {
    return Object.fromEntries(
      Object.entries(collection).map(([key, item], itemIndex) => {
        if (itemIndex !== index) {
          return [key, item];
        }

        return [key, updater(isPlainObject(item) ? { ...item } : { keyword: key, value: item })];
      })
    );
  }

  return collection;
};

const updateNestedPayloadValue = (payload, path, value) => {
  if (!path.length) {
    return value;
  }

  const [currentKey, ...remainingPath] = path;
  const currentPayload = isPlainObject(payload) ? payload : {};
  const currentValue = currentPayload[currentKey];

  return {
    ...currentPayload,
    [currentKey]: remainingPath.length
      ? updateNestedPayloadValue(currentValue, remainingPath, value)
      : value,
  };
};

const prepareReplyRulesForSave = (replyRules) => {
  return normaliseCollection(replyRules).map((rule) => {
    const actionsKey = getFieldKey(rule, ACTION_KEYS, "actions");
    const actions = getRuleActions(rule).map((action) => {
      const payloadKey = getFieldKey(action, ["action_payload", "actionPayload", "payload", "data"], "action_payload");
      const actionType = readFirstDefined(action, ["action_type", "actionType", "type", "name"]);
      const delayTypeKey = getFieldKey(action, ["delay_type", "delayType", "delay"], "delay_type");
      const payload = normalizePayload(action[payloadKey]);

      return {
        ...action,
        [delayTypeKey]: normaliseDelayType(action[delayTypeKey]),
        [payloadKey]: isSendSmsAction(actionType)
          ? {
              ...payload,
              message: limitMessageLength(
                readFirstDefined(payload, ["message", "body", "sms_message", "smsMessage"]) || getPayloadMessage(action)
              ),
            }
          : payload,
      };
    });

    return {
      ...rule,
      [actionsKey]: actions,
    };
  });
};

const normaliseCollection = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "object") {
    return Object.entries(value).map(([key, item]) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return { keyword: key, ...item };
      }

      return { keyword: key, value: item };
    });
  }

  return [];
};

const normaliseFlow = (flow) => {
  const template = readFirstDefined(flow, ["template", "automation_template", "automationTemplate"]) || {};
  const event = readFirstDefined(flow, ["event", "event_details", "eventDetails"]) || {};
  const eventId =
    readFirstDefined(flow, ["event_id", "eventId"]) ||
    readFirstDefined(event, ["id", "event_id", "eventId"]);

  return {
    ...flow,
    id: readFirstDefined(flow, ["id", "flow_id", "flowId", "automation_flow_id", "automationFlowId"]),
    name: readFirstDefined(flow, ["name"]) || "",
    templateName:
      readFirstDefined(flow, ["template_name", "templateName"]) ||
      readFirstDefined(template, ["name", "template_name", "templateName", "title"]),
    status: normaliseAutomationFlowStatus(flow),
    eventId: eventId ? String(eventId) : "",
    eventName:
      readFirstDefined(flow, ["event_name", "eventName"]) ||
      readFirstDefined(event, ["name", "event_name", "eventName", "title"]),
    triggerType: readFirstDefined(flow, [
      "trigger",
      "trigger_type",
      "triggerType",
      "event_type",
      "eventType",
    ]),
    initialMessage:
      readFirstDefined(flow, [
        "initial_sms_message",
        "initialSmsMessage",
        "initial_message",
        "initialMessage",
        "sms_message",
        "smsMessage",
        "message",
      ]) || "",
    initialMessageSendTime: normaliseInitialMessageSendTime(
      readFirstDefined(flow, ["initial_message_send_time", "initialMessageSendTime"])
    ),
    fieldKeys: {
      name: findFieldKey(flow, ["name", "flow_name", "flowName", "title"]) || "name",
      eventId: findFieldKey(flow, ["event_id", "eventId"]) || "event_id",
      initialMessage:
        findFieldKey(flow, [
          "initial_sms_message",
          "initialSmsMessage",
          "initial_message",
          "initialMessage",
          "sms_message",
          "smsMessage",
          "message",
        ]) || "initial_sms_message",
      triggerType: findFieldKey(flow, ["trigger", "trigger_type", "triggerType", "event_type", "eventType"]),
      replyRules: findFieldKey(flow, SECTION_FIELDS.replyRules),
      ruleActions: findFieldKey(flow, SECTION_FIELDS.ruleActions),
      reminderActions: findFieldKey(flow, SECTION_FIELDS.reminderActions),
      notificationActions: findFieldKey(flow, SECTION_FIELDS.notificationActions),
    },
    replyRules: readFirstDefined(flow, SECTION_FIELDS.replyRules),
    ruleActions: readFirstDefined(flow, SECTION_FIELDS.ruleActions),
    reminderActions: readFirstDefined(flow, SECTION_FIELDS.reminderActions),
    notificationActions: readFirstDefined(flow, SECTION_FIELDS.notificationActions),
  };
};

const normaliseEvent = (event) => ({
  ...event,
  id: readFirstDefined(event, ["id", "event_id", "eventId", "uuid"]),
  name: readFirstDefined(event, ["event_name", "eventName", "name", "title"]),
});

const normaliseEventsResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.event ||
    source.events ||
    source.live_events ||
    source.liveEvents ||
    source.data ||
    (Array.isArray(source) ? source : []);
  const items = Array.isArray(collection) ? collection : collection.data || [];

  return items.map(normaliseEvent);
};

const AutomationFlowPlaceholder = () => {
  const { flowId } = useParams();
  const history = useHistory();
  const { user } = LocalStorage();
  const [flow, setFlow] = useState(null);
  const [form, setForm] = useState({
    name: "",
    eventId: "",
    initialMessage: "",
    initialMessageSendTime: DEFAULT_INITIAL_MESSAGE_SEND_TIME,
    replyRules: "",
    ruleActions: "",
    reminderActions: "",
    notificationActions: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [validation, setValidation] = useState({});
  const [events, setEvents] = useState([]);

  const isActive = useMemo(() => {
    return flow?.status === "active";
  }, [flow]);
  const isEventLocked = Boolean(flow?.eventId);
  const saveActionLabel = isActive ? "Save Changes" : "Save Draft";

  const loadFlow = useCallback(async (isMounted = () => true) => {
    if (!flowId || flowId === "new") {
      setNotFound(true);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setNotFound(false);
      const response = await fetchAutomationFlow(flowId);
      const loadedFlow = normaliseFlow(extractFlow(response.data));

      if (!loadedFlow.id && !loadedFlow.name) {
        throw new Error("Automation flow not found.");
      }

      if (isMounted()) {
        setFlow(loadedFlow);
        setForm({
          name: loadedFlow.name,
          eventId: loadedFlow.eventId,
          initialMessage: loadedFlow.initialMessage,
          initialMessageSendTime: loadedFlow.initialMessageSendTime,
          replyRules: loadedFlow.replyRules,
          ruleActions: loadedFlow.ruleActions,
          reminderActions: loadedFlow.reminderActions,
          notificationActions: loadedFlow.notificationActions,
        });
      }
    } catch (error) {
      if (isMounted()) {
        const status = error?.response?.status;
        setFlow(null);
        setNotFound(status === 404);
        setError(status === 404 ? "" : "Unable to load automation flow details.");
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [flowId]);

  const loadEvents = useCallback(async (isMounted = () => true) => {
    try {
      const eventsResponse = await fetchLiveEvents({
        per_page: 1000,
        page: 1,
        user_id: user?.id,
      });

      if (isMounted()) {
        setEvents(normaliseEventsResponse(eventsResponse.data));
      }
    } catch (error) {
      if (isMounted()) {
        setEvents([]);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    loadFlow(() => mounted);
    loadEvents(() => mounted);

    return () => {
      mounted = false;
    };
  }, [loadEvents, loadFlow]);

  const handleChange = (field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
    setValidation((currentValidation) => ({
      ...currentValidation,
      [field]: "",
    }));
  };

  const handleReplyRuleChange = (ruleIndex, fieldKey, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      replyRules: updateCollectionAtIndex(currentForm.replyRules, ruleIndex, (rule) => ({
        ...rule,
        [fieldKey]: value,
      })),
    }));
  };

  const handleActionPayloadChange = (ruleIndex, actionIndex, path, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      replyRules: updateCollectionAtIndex(currentForm.replyRules, ruleIndex, (rule) => {
        const actionsKey = getFieldKey(rule, ACTION_KEYS, "actions");

        return {
          ...rule,
          [actionsKey]: updateCollectionAtIndex(rule[actionsKey], actionIndex, (action) => {
            const payloadKey = getFieldKey(action, ["action_payload", "actionPayload", "payload", "data"], "action_payload");

            return {
              ...action,
              [payloadKey]: updateNestedPayloadValue(normalizePayload(action[payloadKey]), path, value),
            };
          }),
        };
      }),
    }));
  };

  const handleSmsActionMessageChange = (ruleIndex, actionIndex, value) => {
    handleActionPayloadChange(ruleIndex, actionIndex, ["message"], limitMessageLength(value));
  };

  const buildPayload = () => {
    const payload = {
      [flow.fieldKeys.name]: form.name.trim(),
      [flow.fieldKeys.initialMessage]: limitMessageLength(form.initialMessage).trim(),
      initial_message_send_time: form.initialMessageSendTime,
    };

    if (!isEventLocked) {
      payload[flow.fieldKeys.eventId] = form.eventId;
    }

    if (flow.fieldKeys.triggerType) {
      payload[flow.fieldKeys.triggerType] = flow.triggerType;
    }

    ["replyRules", "ruleActions", "reminderActions", "notificationActions"].forEach((section) => {
      if (flow.fieldKeys[section]) {
        payload[flow.fieldKeys[section]] = section === "replyRules"
          ? prepareReplyRulesForSave(form.replyRules)
          : form[section];
      }
    });

    return payload;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const nextValidation = {};

    if (!form.name.trim()) {
      nextValidation.name = "Flow name is required.";
    }

    if (!form.eventId) {
      nextValidation.eventId = "Event is required.";
    }

    if (!form.initialMessageSendTime) {
      nextValidation.initialMessageSendTime = "Initial message send time is required.";
    }

    if (Object.keys(nextValidation).length) {
      setValidation(nextValidation);
      return;
    }

    try {
      setSaving(true);
      await updateAutomationFlow(flow.id || flowId, buildPayload());
      toast.success("Automation flow saved.");
      await loadFlow();
    } catch (error) {
      const eventIdError = getValidationMessage(error, "event_id");
      const initialMessageSendTimeError = getValidationMessage(error, "initial_message_send_time");

      if (eventIdError || initialMessageSendTimeError) {
        setValidation((currentValidation) => ({
          ...currentValidation,
          ...(eventIdError ? { eventId: eventIdError } : {}),
          ...(initialMessageSendTimeError ? { initialMessageSendTime: initialMessageSendTimeError } : {}),
        }));
      }
      toast.error("Unable to save automation flow.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!form.initialMessageSendTime) {
      setValidation((currentValidation) => ({
        ...currentValidation,
        initialMessageSendTime: "Initial message send time is required.",
      }));
      return;
    }

    try {
      setStatusUpdating(true);
      await updateAutomationFlow(flow.id || flowId, {
        initial_message_send_time: form.initialMessageSendTime,
      });
      await publishAutomationFlow(flow.id || flowId);
      toast.success("Automation flow published.");
      await loadFlow();
    } catch (error) {
      const eventIdError = getValidationMessage(error, "event_id");
      const initialMessageSendTimeError = getValidationMessage(error, "initial_message_send_time");

      if (eventIdError || initialMessageSendTimeError) {
        setValidation((currentValidation) => ({
          ...currentValidation,
          ...(eventIdError ? { eventId: eventIdError } : {}),
          ...(initialMessageSendTimeError ? { initialMessageSendTime: initialMessageSendTimeError } : {}),
        }));
      }
      toast.error("Unable to publish automation flow.");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handlePause = async () => {
    try {
      setStatusUpdating(true);
      await pauseAutomationFlow(flow.id || flowId);
      toast.success("Automation flow paused.");
      await loadFlow();
    } catch (error) {
      toast.error("Unable to pause automation flow.");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      const response = await duplicateAutomationFlow(flow.id || flowId);
      const duplicatedFlow = normaliseFlow(extractFlow(response.data));

      if (!duplicatedFlow.id) {
        throw new Error("The duplicated automation flow response did not include an ID.");
      }

      toast.success("Automation flow duplicated.");
      history.push(`/conversions/automation-flows/${duplicatedFlow.id}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to duplicate automation flow.";
      toast.error(message);
    } finally {
      setDuplicating(false);
    }
  };

  const renderRuleActions = (actions, ruleIndex) => {
    const normalisedActions = Array.isArray(actions) ? actions : normaliseCollection(actions);

    if (!normalisedActions.length) {
      return <p className="mb-0 text-muted">No actions configured.</p>;
    }

    return (
      <div className="automation-rule-actions">
        {normalisedActions.map((action, index) => {
          const actionType = readFirstDefined(action, ["action_type", "actionType", "type", "name"]);
          const isSmsAction = isSendSmsAction(actionType);
          const smsMessage = limitMessageLength(getPayloadMessage(action));

          return (
            <div className="automation-rule-action" key={readFirstDefined(action, ["id"]) || `${actionType}-${index}`}>
              <div className="automation-rule-action-title mb-3">
                <div className="form-group mb-0 flex-grow-1">
                  <label className="form-label">What happens next</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formatActionType(actionType)}
                    readOnly
                  />
                </div>
              </div>

              {isSmsAction ? (
                <div className="form-group mb-0 automation-reply-sms-field">
                  <label className="form-label">SMS Message</label>
                  <div className="automation-message-field">
                    <textarea
                      className="form-control"
                      rows="5"
                      value={smsMessage}
                      maxLength={MESSAGE_CHARACTER_LIMIT}
                      onChange={(event) => handleSmsActionMessageChange(ruleIndex, index, event.target.value)}
                    />
                    <span className="automation-message-character-count">
                      {smsMessage.length}/{MESSAGE_CHARACTER_LIMIT}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="mb-0 text-muted">No editable SMS message for this action.</p>
              )}

            </div>
          );
        })}
      </div>
    );
  };

  const renderReplyRules = () => {
    const replyRules = normaliseCollection(form.replyRules);

    return (
      <div className="automation-builder-replies">
        {!replyRules.length ? (
          <p className="mb-0 text-muted">No reply rules configured.</p>
        ) : (
          <div className="automation-reply-rule-list">
            {replyRules.map((rule, index) => {
              const keyword = readFirstDefined(rule, [
                "reply_keyword",
                "replyKeyword",
                "keyword",
                "name",
                "value",
              ]);
              const keywordKey = getFieldKey(rule, [
                "reply_keyword",
                "replyKeyword",
                "keyword",
                "name",
                "value",
              ], "reply_keyword");
              const actions = getRuleActions(rule);
              const roiCategoryKey = getFieldKey(rule, ["roi_category", "roiCategory"], "roi_category");
              const roiCategory = readFirstDefined(rule, ["roi_category", "roiCategory"]) || "";

              return (
                <div className={`card automation-reply-rule-card reply-tone-${index % 3}`} key={readFirstDefined(rule, ["id"]) || `${keyword}-${index}`}>
                  <header className="automation-reply-card-heading">
                    <span><FiMessageSquare /></span>
                    <strong>{keyword || `Reply ${index + 1}`}</strong>
                  </header>
                  <div className="card-body">
                    <div className="automation-reply-rule-header">
                      <div className="automation-rule-fields">
                        <div className="form-group mb-0 automation-reply-field-row">
                          <label className="form-label">Guest replies</label>
                          <input
                            type="text"
                            className="form-control"
                            value={keyword || ""}
                            onChange={(event) => handleReplyRuleChange(index, keywordKey, event.target.value)}
                          />
                        </div>
                        <div className="form-group mb-0 automation-reply-field-row">
                          <label className="form-label">Category</label>
                          <select
                            className="form-control"
                            value={roiCategory}
                            onChange={(event) => handleReplyRuleChange(index, roiCategoryKey, event.target.value)}
                          >
                            <option value="">Not counted as commercial intent</option>
                            {ROI_CATEGORY_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="automation-rule-actions-section">
                      <h6>Actions</h6>
                      {renderRuleActions(actions, index)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="card radius-10">
          <div className="card-body">
            <div className="my-5 text-center">
              <span className="spinner-border" role="status" aria-hidden="true"></span>
            </div>
          </div>
        </div>
      );
    }

    if (notFound) {
      return (
        <div className="card radius-10">
          <div className="card-body text-center py-5">
            <h5 className="mb-2">Automation flow not found</h5>
            <p className="text-muted">This automation flow is unavailable or may have been removed.</p>
            <Link to="/conversions/automation-flows" className="btn btn-primary">
              Back to My Automation Flows
            </Link>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="card radius-10">
          <div className="card-body text-center py-5">
            <h5 className="mb-2">Unable to load automation flow</h5>
            <p className="text-muted">{error}</p>
            <button type="button" className="btn btn-primary" onClick={() => loadFlow()}>
              Try Again
            </button>
          </div>
        </div>
      );
    }

    if (!flow) {
      return null;
    }

    return (
      <div className="revenue-loop-builder">
        <header className="revenue-loop-builder-header">
          <div className="revenue-loop-builder-title">
            <span className="revenue-loop-builder-logo"><FiMessageSquare /></span>
            <div>
              <div className="revenue-loop-builder-title-row">
                <h1>{flow.name || "Untitled Revenue Loop"}</h1>
                <span className={`automation-flow-status status-${flow.status}`}>
                  {formatStatus(flow.status)}
                </span>
              </div>
              <p>Create and manage your follow-up SMS flow in four easy steps.</p>
            </div>
          </div>

          <ol className="revenue-loop-progress" aria-label="Flow setup steps">
            {["Set Up", "Initial SMS", "Timing", "Replies"].map((label, index) => (
              <li key={label}>
                <span>{index + 1}</span>
                <small>{label}</small>
              </li>
            ))}
          </ol>

          <div className="revenue-loop-header-actions">
            <button
              type="submit"
              form="automation-flow-builder-form"
              className="btn revenue-loop-outline-button"
              disabled={saving}
              aria-label={saveActionLabel}
            >
              <FiSave /> {saving ? "Saving..." : saveActionLabel}
            </button>
          </div>
        </header>

        <form id="automation-flow-builder-form" onSubmit={handleSave}>
          {isActive ? (
            <div className="revenue-loop-live-edit-notice" role="status">
              <FiHelpCircle />
              <span><strong>This flow is active.</strong> Saved changes will take effect immediately.</span>
            </div>
          ) : null}

          <section className="revenue-loop-step-card">
            <div className="revenue-loop-step-intro">
              <span className="revenue-loop-step-number">1</span>
              <span className="revenue-loop-step-icon"><FiFileText /></span>
              <div>
                <h2>Set Up Your Flow</h2>
                <p>Give your flow a clear name and select the event.</p>
              </div>
            </div>
            <div className="revenue-loop-step-content revenue-loop-setup-grid">
                <div className="form-group">
                  <label className="form-label">Flow Name</label>
                  <input
                    type="text"
                    className={`form-control ${validation.name ? "is-invalid" : ""}`}
                    value={form.name}
                    onChange={(event) => handleChange("name", event.target.value)}
                  />
                  {validation.name ? <div className="invalid-feedback">{validation.name}</div> : null}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="automation-flow-event">Event</label>
                  <select
                    id="automation-flow-event"
                    className={`form-control ${validation.eventId ? "is-invalid" : ""}`}
                    value={form.eventId}
                    onChange={(event) => handleChange("eventId", event.target.value)}
                    disabled={isEventLocked}
                    required
                  >
                    <option value="">Select event</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.name || `Event #${event.id}`}
                      </option>
                    ))}
                    {flow.eventId && !events.some((event) => String(event.id) === String(flow.eventId)) ? (
                      <option value={flow.eventId}>
                        {flow.eventName || `Event #${flow.eventId}`}
                      </option>
                    ) : null}
                  </select>
                  {validation.eventId ? <div className="invalid-feedback">{validation.eventId}</div> : null}
                  {isEventLocked ? (
                    <small className="form-text text-muted">Duplicate this flow to use it with another event.</small>
                  ) : null}
                </div>
            </div>
          </section>

          <section className="revenue-loop-step-card">
            <div className="revenue-loop-step-intro">
              <span className="revenue-loop-step-number">2</span>
              <span className="revenue-loop-step-icon"><FiMessageSquare /></span>
              <div>
                <h2>Write Your First SMS</h2>
                <p>This is the first message your guests will receive.</p>
              </div>
            </div>
            <div className="revenue-loop-step-content revenue-loop-message-grid">
              <div className="form-group">
                <label className="form-label">Message guests first</label>
                <div className="automation-message-field">
                  <textarea
                    className="form-control"
                    rows="4"
                    value={limitMessageLength(form.initialMessage)}
                    maxLength={MESSAGE_CHARACTER_LIMIT}
                    onChange={(event) => handleChange("initialMessage", limitMessageLength(event.target.value))}
                  />
                  <span className="automation-message-character-count">
                    {limitMessageLength(form.initialMessage).length}/{MESSAGE_CHARACTER_LIMIT}
                  </span>
                </div>
              </div>
              <aside className="revenue-loop-tips">
                <strong><FiHelpCircle /> Tips</strong>
                <ul>
                  <li>Keep it short and friendly</li>
                  <li>Tell guests how to reply</li>
                  <li>Best results: give guests 2 simple reply options</li>
                </ul>
              </aside>
            </div>
          </section>

          <section className="revenue-loop-step-card">
            <div className="revenue-loop-step-intro">
              <span className="revenue-loop-step-number">3</span>
              <span className="revenue-loop-step-icon"><FiClock /></span>
              <div>
                <h2>When Should We Send?</h2>
                <p>Choose the best time to send your first message.</p>
              </div>
            </div>
            <div className="revenue-loop-step-content revenue-loop-timing-content">
              <InitialMessageSendTimeSelect
                value={form.initialMessageSendTime}
                error={validation.initialMessageSendTime}
                onChange={(value) => handleChange("initialMessageSendTime", value)}
              />
            </div>
          </section>

          <section className="revenue-loop-step-card revenue-loop-replies-step">
            <div className="revenue-loop-step-intro">
              <span className="revenue-loop-step-number">4</span>
              <span className="revenue-loop-step-icon"><FiCornerUpLeft /></span>
              <div>
                <h2>Set Up Guest Replies</h2>
                <p>Choose how to respond based on what your guests send.</p>
              </div>
            </div>
            <div className="revenue-loop-step-content">
              {renderReplyRules()}
            </div>
          </section>

          <div className="revenue-loop-form-actions">
            <Link to="/conversions/automation-flows" className="btn revenue-loop-back-button">
              Back
            </Link>
            <button
              type="submit"
              className="btn revenue-loop-outline-button"
              disabled={saving}
              aria-label={saveActionLabel}
            >
              <FiSave /> {saving ? "Saving..." : saveActionLabel}
            </button>
            <button
              type="button"
              className="btn revenue-loop-outline-button"
              disabled={duplicating || statusUpdating}
              onClick={handleDuplicate}
            >
              <FiCopy /> {duplicating ? "Duplicating..." : "Duplicate"}
            </button>
            {isActive ? (
              <button
                type="button"
                className="btn revenue-loop-pause-button"
                disabled={statusUpdating}
                onClick={handlePause}
              >
                <FiSettings /> {statusUpdating ? "Updating..." : "Pause Flow"}
              </button>
            ) : (
              <button
                type="button"
                className="btn revenue-loop-publish-button"
                disabled={statusUpdating}
                onClick={handlePublish}
                aria-label="Publish"
              >
                <FiSend /> {statusUpdating ? "Updating..." : "Publish Flow"}
              </button>
            )}
          </div>
        </form>
      </div>
    );
  };

  return (
    <SmsAccessGuard>
      <Layout>
        <div className="page">
        {renderContent()}
        <ToastContainer position="bottom-right" autoClose={2500} />
        </div>
      </Layout>
    </SmsAccessGuard>
  );
};

export default AutomationFlowPlaceholder;
